import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Upload, Download, Eye, Trash2, FileText, FileImage, FileSpreadsheet, AlertTriangle, Loader2, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { projectsApi, EvidenceFile } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canDownload, canUpload as canUploadFiles, canManage } from '@/lib/roles'
import { useTranslation } from 'react-i18next'
import {
  EVIDENCE_ALLOWED_EXTENSIONS,
  EVIDENCE_ALLOWED_LABEL,
  EVIDENCE_SOFT_SIZE_WARNING_BYTES,
  EVIDENCE_SOFT_SIZE_WARNING_LABEL,
  isEvidenceTypeAllowed,
  canPreviewInline,
  formatFileSize,
  getEvidenceExtension,
} from '@/lib/evidenceFileTypes'

interface Props {
  projectSystemId: string
  // external drops routed by the page-level useDocumentDropRouter
  // hand off here. an upload triggered from outside the section
  // (e.g. a PDF dropped on the Mappings tab) refetches the list when
  // done so the new row shows even if the user later navigates
  // back to the Evidence tab.
  externalUploadSignal?: number
}

// Phase 3.6.1: top-level evidence section on System Detail.
// renders for every system (profile or not). the component-scoped
// drop zone from 6342a87 was REMOVED in commit (this one) because
// it raced against the page-root drop handler, two listeners
// firing for the same drop produced double-uploads. the page-level
// useDocumentDropRouter is the only drop entry point now.
//
// inline preview: PDFs and images expand BENEATH their row in an
// accordion (one open at a time, iframe memory pressure). the
// previous EvidencePreviewModal was a UX dead-end on Firefox,
// some users hit the X-Frame-Options block before the backend
// frame-headers fix landed.
export interface EvidenceSectionHandle {
  upload: (files: File[]) => Promise<void>
}

export const EvidenceSection = React.forwardRef<EvidenceSectionHandle, Props>(
  function EvidenceSection({ projectSystemId, externalUploadSignal }, ref) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { success, error: showError, showToast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canUpload = canUploadFiles(user)
  const mayDownload = canDownload(user)
  const canDelete = canManage(user)

  const [files,    setFiles]    = useState<EvidenceFile[]>([])
  const [loading,  setLoading]  = useState(true)
  const [uploading, setUploading] = useState(false)
  // accordion: id of the currently-expanded row, or null
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // preview URLs signed per file on first expand, cached for the
  // duration of the mount. tokens are 5-minute, plenty for a single
  // page session. map keyed by file id so collapsing then re-expanding
  // doesn't refetch.
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({})

  const refetch = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await projectsApi.listSystemEvidence(projectSystemId)
      setFiles(rows)
    } catch (err: any) {
      showError(err?.message ?? t('wordRa.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectSystemId, showError, t])

  useEffect(() => { refetch() }, [refetch])

  // refetch when the parent page signals an external upload completed
  useEffect(() => {
    if (externalUploadSignal !== undefined && externalUploadSignal > 0) {
      refetch()
    }
  }, [externalUploadSignal, refetch])

  // shared upload routine, called by the in-section Upload button
  // and (via the imperative handle) by the page-level drop router.
  // pre-checks run before any network call.
  const doUpload = useCallback(async (rawFiles: File[]) => {
    if (rawFiles.length === 0) return

    const rejected: string[] = []
    const oversize: string[] = []
    for (const f of rawFiles) {
      if (!isEvidenceTypeAllowed(f.name)) rejected.push(f.name)
      else if (f.size > EVIDENCE_SOFT_SIZE_WARNING_BYTES) oversize.push(f.name)
    }
    if (rejected.length > 0) {
      showError(t('wordRa.typeNotAllowed', { files: rejected.join(', '), types: EVIDENCE_ALLOWED_LABEL }))
    }
    if (oversize.length > 0) {
      showToast({
        type: 'warning',
        title: t('wordRa.largeFiles'),
        message: t('wordRa.largeFilesMessage', { files: oversize.join(', '), size: EVIDENCE_SOFT_SIZE_WARNING_LABEL }),
        duration: 8000,
      })
    }

    const queue = rawFiles.filter(f => isEvidenceTypeAllowed(f.name))
    if (queue.length === 0) return

    setUploading(true)
    const created: EvidenceFile[] = []
    for (const file of queue) {
      try {
        const row = await projectsApi.uploadSystemEvidence(projectSystemId, file)
        created.push(row)
      } catch (err: any) {
        showError(t('wordRa.uploadFailed', { file: file.name, error: err?.message ?? t('contactsPage.unknown') }))
      }
    }
    setUploading(false)

    if (created.length > 0) {
      success(t('wordRa.uploaded', { count: created.length }))
      await refetch()
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [projectSystemId, refetch, success, showError, showToast, t])

  // imperative handle so the page-level drop router can call us
  // without re-implementing the upload pipeline
  React.useImperativeHandle(ref, () => ({ upload: doUpload }), [doUpload])

  const handleDelete = async (file: EvidenceFile) => {
    if (!confirm(t('wordRa.deleteConfirm', { file: file.original_filename }))) return
    try {
      await projectsApi.deleteEvidence(file.id)
      success(t('wordRa.deleted'))
      setFiles(prev => prev.filter(f => f.id !== file.id))
      if (expandedId === file.id) setExpandedId(null)
    } catch (err: any) {
      showError(err?.message ?? t('wordRa.deleteFailed'))
    }
  }

  const handleDownload = async (file: EvidenceFile) => {
    try {
      const url = await projectsApi.signEvidenceDownload(file.id)
      window.location.href = `${url}&inline=0`
    } catch (err: any) {
      showError(err?.message ?? t('wordRa.downloadFailed'))
    }
  }

  // toggle a row's expand state. opening one row closes any other
  // (accordion). PDFs in iframes are heavy, one at a time keeps
  // memory bounded on long evidence lists.
  const toggleExpand = useCallback(async (file: EvidenceFile) => {
    if (!canPreviewInline(file)) return
    if (expandedId === file.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(file.id)
    if (!previewUrls[file.id]) {
      try {
        const url = await projectsApi.signEvidenceDownload(file.id)
        setPreviewUrls(prev => ({ ...prev, [file.id]: url }))
      } catch (err: any) {
        showError(err?.message ?? t('wordRa.previewFailed'))
        setExpandedId(null)
      }
    }
  }, [expandedId, previewUrls, showError, t])

  return (
    <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div className="px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[13px] font-medium">{t('wordRa.evidence')}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {t('wordRa.evidenceHint', { types: EVIDENCE_ALLOWED_LABEL })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
            {t('wordRa.fileCount', { count: files.length })}
          </span>
          {canUpload && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-[10px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {uploading ? t('wordRa.uploading') : t('wordRa.upload')}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={EVIDENCE_ALLOWED_EXTENSIONS.map(e => `.${e}`).join(',')}
            className="hidden"
            onChange={e => e.target.files && doUpload(Array.from(e.target.files))}
          />
        </div>
      </div>

      {loading ? (
        <div className="px-3.5 py-6 text-center text-xs text-slate-400">{t('wordRa.loadingEvidence')}</div>
      ) : files.length === 0 ? (
        <div className="px-3.5 py-6 text-center text-xs text-slate-400">
          {t('wordRa.noEvidence')} {canUpload && t('wordRa.uploadHint')}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
          {files.map(f => (
            <EvidenceRow
              key={f.id}
              file={f}
              canDownload={mayDownload}
              canDelete={canDelete}
              expanded={expandedId === f.id}
              previewUrl={previewUrls[f.id]}
              onToggleExpand={() => toggleExpand(f)}
              onDownload={() => handleDownload(f)}
              onDelete={() => handleDelete(f)}
            />
          ))}
        </ul>
      )}
    </div>
  )
})

const EvidenceRow: React.FC<{
  file:           EvidenceFile
  canDownload:    boolean
  canDelete:      boolean
  expanded:       boolean
  previewUrl:     string | undefined
  onToggleExpand: () => void
  onDownload:     () => void
  onDelete:       () => void
}> = ({ file, canDownload: mayDownload, canDelete, expanded, previewUrl, onToggleExpand, onDownload, onDelete }) => {
  const { t, i18n } = useTranslation()
  const ext = getEvidenceExtension(file.original_filename)
  const previewable = canPreviewInline(file)
  const Icon
    = ext === 'png' || ext === 'jpg' || ext === 'jpeg' ? FileImage
    : ext === 'xlsx' || ext === 'xls'                  ? FileSpreadsheet
    :                                                    FileText
  const isLarge = file.file_size_bytes > EVIDENCE_SOFT_SIZE_WARNING_BYTES
  const isPdf   = (file.mime_type ?? '') === 'application/pdf'
               || ext === 'pdf'

  return (
    <li>
      <div
        className={
          'px-3.5 py-2 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 ' +
          (previewable ? 'cursor-pointer' : '')
        }
        onClick={previewable ? onToggleExpand : undefined}
      >
        {previewable ? (
          expanded
            ? <ChevronDown  className="h-3 w-3 text-slate-400 shrink-0" />
            : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
        ) : (
          <span className="w-3 shrink-0" aria-hidden />
        )}
        <Icon className="h-4 w-4 text-slate-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">
            {file.original_filename}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
            <span className={isLarge ? 'text-amber-600 dark:text-amber-400 inline-flex items-center gap-1' : ''}>
              {isLarge && <AlertTriangle className="h-2.5 w-2.5" />}
              {formatFileSize(file.file_size_bytes)}
            </span>
            <span>·</span>
            <span>{new Date(file.uploaded_at).toLocaleDateString(i18n.resolvedLanguage)}</span>
            {file.uploader_email && (
              <>
                <span>·</span>
                <span className="truncate">{file.uploader_email}</span>
              </>
            )}
            {file.attached_to_type !== 'general' && (
              <>
                <span>·</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 uppercase tracking-wide">
                  {file.attached_to_type === 'cbs_question' ? 'CBS' : 'Vendor'}
                  {file.attached_to_key ? ` · ${file.attached_to_key}` : ''}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          {previewable && mayDownload && (
            <button
              onClick={onToggleExpand}
              className="text-[10px] px-1.5 py-0.5 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-1"
              title={t('wordRa.previewInPage')}
            >
              <Eye className="h-3 w-3" />
              {expanded ? t('wordRa.hide') : t('wordRa.preview')}
            </button>
          )}
          {mayDownload && (
            <button
              onClick={onDownload}
              className="text-[10px] px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-1"
              title={t('wordRa.download')}
            >
              <Download className="h-3 w-3" />
              {t('wordRa.download')}
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="text-[10px] px-1.5 py-0.5 rounded text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 flex items-center gap-1"
              title={t('wordRa.delete')}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* inline preview pane, accordion. iframe / img mounted only
          when expanded so PDF rendering memory is reclaimed on
          collapse. */}
      {expanded && previewable && (
        <div className="border-t border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/40">
          {!previewUrl ? (
            <div className="h-32 flex items-center justify-center text-xs text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('wordRa.loadingPreview')}
            </div>
          ) : isPdf ? (
            <iframe
              src={previewUrl}
              title={file.original_filename}
              className="w-full border-0 bg-white dark:bg-slate-800"
              style={{ height: '70vh', maxHeight: '70vh' }}
            />
          ) : (
            <div className="flex items-center justify-center p-3">
              <img
                src={previewUrl}
                alt={file.original_filename}
                className="max-w-full object-contain rounded"
                style={{ maxHeight: '70vh' }}
              />
            </div>
          )}
          {previewUrl && (
            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end">
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="h-2.5 w-2.5" />
                {t('wordRa.openNewTab')}
              </a>
            </div>
          )}
        </div>
      )}
    </li>
  )
}
